import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Initialize' },
    { label: 'Sign Document' },
    { label: 'Get Status' },
    { label: 'Signed Document' },
];

const AUTH_MODES = [
    { label: 'OTP', value: 'OTP' },
    { label: 'Aadhaar XML', value: 'AADHAAR_XML' },
    { label: 'Biometric', value: 'BIOMETRIC' },
];

const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10 MB

const DEFAULT_POSITION = { page_number: 1, x: 100, y: 100, width: 200, height: 100 };

const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const EsignFlowComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const pdfInputRef = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // Shared session token passed between steps
    const [clientId, setClientId] = useState('');
    const [signingUrl, setSigningUrl] = useState('');

    // Per-step API results
    const [statusResult, setStatusResult] = useState(null);
    const [signedDocResult, setSignedDocResult] = useState(null);

    // Step 0 form
    const [form, setForm] = useState({
        full_name: '', mobile_number: '', user_email: '',
        auth_mode: 'OTP', reason: '', callback_url: window.location.href, consent: false,
    });
    const [pdfFile, setPdfFile] = useState(null);
    const [positions, setPositions] = useState([{ ...DEFAULT_POSITION }]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${serviceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [serviceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const msgs = { 500: 'Server error. Try again.', 422: 'Check your inputs.', 429: 'Too many requests.', 400: 'Invalid input.', 404: 'No record found.' };
        let detail = msgs[s] || error.response?.data?.message || error.message || summary;
        if (s === 402) {
            const c = error.response?.data || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    const setField = (name, transform) => (e) => {
        let v = e.target ? e.target.value : e.value;
        if (transform) v = transform(String(v));
        setForm((p) => ({ ...p, [name]: v }));
        if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    };

    const setPositionField = (index, field, value) => {
        setPositions((prev) => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
    };

    const addPosition = () => setPositions((p) => [...p, { ...DEFAULT_POSITION }]);
    const removePosition = (index) => setPositions((p) => p.filter((_, i) => i !== index));

    const validateInit = () => {
        const next = {};
        if (!form.full_name.trim()) next.full_name = 'Full Name is required';
        if (!form.mobile_number) next.mobile_number = 'Mobile Number is required';
        else if (!/^[6-9]\d{9}$/.test(form.mobile_number)) next.mobile_number = 'Enter a valid 10-digit Indian mobile';
        if (!form.user_email.trim()) next.user_email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.user_email)) next.user_email = 'Enter a valid email';
        if (!form.auth_mode) next.auth_mode = 'Auth mode is required';
        if (!pdfFile) next.pdf = 'PDF document is required';
        else if (pdfFile.size > MAX_PDF_SIZE) next.pdf = 'PDF must be under 10 MB';
        if (positions.length === 0) next.positions = 'At least one signature position is required';
        if (!form.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const doInitialize = async () => {
        setLoading(true);
        try {
            const pdfBase64 = await readFileAsBase64(pdfFile);
            const body = {
                pdf_pre_uploaded: false,
                document: pdfBase64,
                callback_url: form.callback_url.trim() || window.location.href,
                config: {
                    auth_mode: form.auth_mode,
                    reason: form.reason.trim() || 'Document signing',
                    positions: positions.map((p) => ({
                        page_number: parseInt(p.page_number, 10),
                        x: parseFloat(p.x),
                        y: parseFloat(p.y),
                        width: parseFloat(p.width),
                        height: parseFloat(p.height),
                    })),
                },
                prefill_options: {
                    full_name: form.full_name.trim(),
                    mobile_number: form.mobile_number,
                    user_email: form.user_email.trim(),
                },
                api_provider_service_id: parseInt(serviceId, 10),
            };
            const res = await axiosInstance.post('/surepass/esign/initialize', body);
            if (!res.data.success) throw new Error(res.data.message || 'Initialization failed');
            const d = res.data?.data?.data || res.data?.data || {};
            setClientId(d.client_id || d.clientId || '');
            setSigningUrl(d.url || d.signing_url || d.sign_url || d.redirect_url || '');
            toast.current?.show({ severity: 'success', summary: 'E-Sign initialized', detail: 'Open the signing URL to sign the document', life: 3000 });
            setStep(1);
        } catch (err) {
            showError(err, 'Initialization failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleInitSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const errs = validateInit();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Name:</span> <span className="text-900 font-medium">{form.full_name}</span></div>
                    <div className="mb-2"><span className="text-600">Mobile:</span> <span className="text-900 font-medium">{form.mobile_number}</span></div>
                    <div className="mb-2"><span className="text-600">Document:</span> <span className="text-900 font-medium">{pdfFile?.name}</span></div>
                    <div className="mb-2"><span className="text-600">Auth Mode:</span> <span className="text-900 font-medium">{form.auth_mode}</span></div>
                    <div className="mb-2"><span className="text-600">Signature positions:</span> <span className="text-900 font-medium">{positions.length}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm E-Sign initialization',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, initialize',
            rejectLabel: 'Cancel',
            accept: doInitialize,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const doGetStatus = async () => {
        setLoading(true);
        setStatusResult(null);
        try {
            const res = await axiosInstance.post('/surepass/esign/get-status', {
                client_id: clientId,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Status check failed');
            setStatusResult(res.data);
        } catch (err) {
            showError(err, 'Status check failed');
        } finally {
            setLoading(false);
        }
    };

    const doGetSignedDoc = async () => {
        setLoading(true);
        setSignedDocResult(null);
        try {
            const res = await axiosInstance.post('/surepass/esign/get-signed-document', {
                client_id: clientId,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Failed to retrieve signed document');
            setSignedDocResult(res.data);
        } catch (err) {
            showError(err, 'Get signed document failed');
        } finally {
            setLoading(false);
        }
    };

    const resetFlow = () => {
        setStep(0);
        setClientId('');
        setSigningUrl('');
        setStatusResult(null);
        setSignedDocResult(null);
        setPdfFile(null);
        setPositions([{ ...DEFAULT_POSITION }]);
        setForm({ full_name: '', mobile_number: '', user_email: '', auth_mode: 'OTP', reason: '', callback_url: window.location.href, consent: false });
        setErrors({});
    };

    const renderStep0 = () => (
        <Card>
            <form onSubmit={handleInitSubmit} noValidate>
                {/* ── Signer Details ─────────────────────────────────────────── */}
                <div className="grid">
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="esFullName" className="block font-medium text-sm mb-2">Full Name <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-user" />
                            <InputText id="esFullName" value={form.full_name} onChange={setField('full_name')} placeholder="Enter full name" maxLength={100} disabled={loading}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.full_name })} />
                        </IconField>
                        {errors.full_name && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.full_name}</small>}
                    </div>
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="esMobile" className="block font-medium text-sm mb-2">Mobile Number <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-phone" />
                            <InputText id="esMobile" value={form.mobile_number} onChange={setField('mobile_number', (v) => v.replace(/\D/g, '').slice(0, 10))}
                                placeholder="9876543210" inputMode="tel" disabled={loading}
                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.mobile_number })} />
                        </IconField>
                        {errors.mobile_number && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.mobile_number}</small>}
                    </div>
                    <div className="field col-12 md:col-6 lg:col-4">
                        <label htmlFor="esEmail" className="block font-medium text-sm mb-2">Email <span className="text-red-500">*</span></label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-envelope" />
                            <InputText id="esEmail" value={form.user_email} onChange={setField('user_email')} placeholder="user@example.com" type="email" disabled={loading}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.user_email })} />
                        </IconField>
                        {errors.user_email && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.user_email}</small>}
                    </div>
                    <div className="field col-12 md:col-4">
                        <label htmlFor="esAuthMode" className="block font-medium text-sm mb-2">Auth Mode <span className="text-red-500">*</span></label>
                        <Dropdown id="esAuthMode" value={form.auth_mode} options={AUTH_MODES} optionLabel="label" optionValue="value"
                            onChange={setField('auth_mode')} disabled={loading}
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.auth_mode })} />
                        {errors.auth_mode && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.auth_mode}</small>}
                    </div>
                    <div className="field col-12 md:col-8">
                        <label htmlFor="esReason" className="block font-medium text-sm mb-2">Reason</label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-info-circle" />
                            <InputText id="esReason" value={form.reason} onChange={setField('reason')} placeholder="Document signing" maxLength={200} disabled={loading}
                                className="w-full p-inputtext-sm" />
                        </IconField>
                    </div>
                    <div className="field col-12">
                        <label htmlFor="esCallback" className="block font-medium text-sm mb-2">Callback URL</label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-link" />
                            <InputText id="esCallback" value={form.callback_url} onChange={setField('callback_url')} placeholder="https://your-app.com/callback" maxLength={300} disabled={loading}
                                className="w-full p-inputtext-sm" />
                        </IconField>
                        <small className="text-500 block mt-1">Surepass redirects the signer here after signing. Defaults to current page if left blank.</small>
                    </div>
                </div>

                {/* ── PDF Upload ─────────────────────────────────────────────── */}
                <div className="mt-2 mb-3">
                    <label className="block font-medium text-sm mb-2">PDF Document <span className="text-red-500">*</span></label>
                    <input ref={pdfInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                        onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setPdfFile(f);
                            if (errors.pdf) setErrors((p) => ({ ...p, pdf: '' }));
                        }} />
                    <div className={classNames('surface-100 border-round border-1 p-3 flex flex-column gap-2', errors.pdf ? 'border-red-500' : 'surface-border')}>
                        <div className="flex align-items-center justify-content-between gap-2">
                            <div className="flex align-items-center gap-2 min-w-0 flex-1">
                                <i className={`pi ${pdfFile ? 'pi-check-circle text-green-500' : 'pi-file-pdf text-red-400'}`} />
                                <span className="text-sm text-700 overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                    {pdfFile ? pdfFile.name : 'No PDF selected'}
                                </span>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <Button type="button" icon="pi pi-folder-open" label={pdfFile ? 'Change' : 'Choose PDF'} size="small" outlined disabled={loading}
                                    onClick={() => pdfInputRef.current?.click()} />
                                {pdfFile && (
                                    <Button type="button" icon="pi pi-times" size="small" severity="secondary" outlined aria-label="Clear PDF"
                                        disabled={loading} onClick={() => setPdfFile(null)} />
                                )}
                            </div>
                        </div>
                        {pdfFile && (
                            <div className="flex align-items-center gap-2">
                                <Tag severity="danger" value="PDF" className="text-xs" />
                                <span className="text-xs text-500">{(pdfFile.size / 1024).toFixed(1)} KB</span>
                            </div>
                        )}
                        <span className="text-xs text-500">PDF up to 10 MB — this is the document the signer will sign</span>
                    </div>
                    {errors.pdf && <small className="p-error block mt-1"><i className="pi pi-exclamation-circle mr-1" />{errors.pdf}</small>}
                </div>

                {/* ── Signature Positions ────────────────────────────────────── */}
                <div className="mt-3">
                    <div className="flex align-items-center justify-content-between mb-2">
                        <div>
                            <span className="font-medium text-sm">Signature Positions <span className="text-red-500">*</span></span>
                            <span className="text-500 text-xs ml-2">— where the signature box appears on the PDF</span>
                        </div>
                        <Button type="button" icon="pi pi-plus" label="Add Position" size="small" outlined onClick={addPosition} disabled={loading} />
                    </div>
                    {errors.positions && <small className="p-error block mb-2"><i className="pi pi-exclamation-circle mr-1" />{errors.positions}</small>}

                    <div className="flex flex-column gap-2">
                        {positions.map((pos, idx) => (
                            <div key={idx} className="surface-50 border-round border-1 surface-border p-3">
                                <div className="flex align-items-center justify-content-between mb-2">
                                    <span className="text-xs font-semibold text-600 uppercase">Position {idx + 1}</span>
                                    {positions.length > 1 && (
                                        <Button type="button" icon="pi pi-trash" size="small" severity="danger" text aria-label="Remove"
                                            onClick={() => removePosition(idx)} disabled={loading} />
                                    )}
                                </div>
                                <div className="grid">
                                    {[
                                        { field: 'page_number', label: 'Page', min: 1, max: 999, step: 1 },
                                        { field: 'x', label: 'X (left)', min: 0, step: 1 },
                                        { field: 'y', label: 'Y (top)', min: 0, step: 1 },
                                        { field: 'width', label: 'Width', min: 10, step: 1 },
                                        { field: 'height', label: 'Height', min: 10, step: 1 },
                                    ].map(({ field, label, min, max, step }) => (
                                        <div key={field} className="field col-6 md:col-4 lg:col-2 mb-0">
                                            <label className="block text-xs font-medium mb-1 text-600">{label}</label>
                                            <InputNumber value={pos[field]} onValueChange={(e) => setPositionField(idx, field, e.value ?? min)}
                                                min={min} max={max} step={step} disabled={loading}
                                                inputClassName="w-full p-inputtext-sm text-center font-mono"
                                                className="w-full" showButtons buttonLayout="stacked"
                                                incrementButtonClassName="p-button-secondary p-button-sm"
                                                decrementButtonClassName="p-button-secondary p-button-sm" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <small className="text-500 block mt-1">
                        Coordinates are in PDF points (72 pts = 1 inch). X/Y measure from bottom-left of the page.
                        A4 page = 595 × 842 pts. Typical signature box: width 200, height 100.
                    </small>
                </div>

                <ConsentCheckbox
                    checked={form.consent}
                    onChange={(e) => { setForm((p) => ({ ...p, consent: e.checked })); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                    error={errors.consent}
                    reportType="esign-initialize"
                />
                <div className="flex justify-content-end mt-3">
                    <Button type="submit" label="Initialize E-Sign" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                </div>
            </form>
        </Card>
    );

    const renderStep1 = () => (
        <Card>
            <div className="text-center py-4">
                <i className="pi pi-pencil text-6xl text-purple-500 block mb-3" />
                <h3 className="text-900 font-semibold m-0 mb-2">Sign the Document</h3>
                <p className="text-600 text-sm mb-4">Open the signing portal, complete the e-sign process, then return here to verify.</p>
                {clientId && (
                    <div className="mb-4 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                        <i className="pi pi-key text-primary text-sm" />
                        <span className="text-600 text-xs">Session ID:</span>
                        <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
                    </div>
                )}
                <div className="flex flex-column align-items-center gap-3">
                    {signingUrl ? (
                        <Button label="Open Signing Portal" icon="pi pi-external-link" iconPos="right"
                            onClick={() => window.open(signingUrl, '_blank', 'noopener,noreferrer')} />
                    ) : (
                        <p className="text-orange-500 text-sm">Signing URL not available in the response.</p>
                    )}
                    <div className="surface-50 border-round-lg border-1 surface-border p-3 text-sm text-600 max-w-25rem text-left">
                        <i className="pi pi-info-circle mr-2 text-primary" />
                        Complete the e-signing process in the portal, then return here and click Continue.
                    </div>
                    <Button
                        label="I've Signed the Document — Continue"
                        icon="pi pi-check-circle"
                        severity="success"
                        onClick={() => { setStep(2); doGetStatus(); }}
                    />
                </div>
            </div>
        </Card>
    );

    const renderStep2 = () => (
        <div>
            {loading && !statusResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Checking e-sign status...</p>
                    </div>
                </Card>
            )}
            {statusResult && <GenericSurepassOutputComponent reportData={statusResult} title="E-Sign Status" icon="pi-pencil" />}
            {!loading && !statusResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Status check failed. Make sure you have completed signing.</p>
                        <Button label="Retry Status Check" icon="pi pi-refresh" outlined onClick={doGetStatus} />
                    </div>
                </Card>
            )}
            {statusResult && (
                <div className="flex justify-content-end mt-3">
                    <Button label="Get Signed Document" icon="pi pi-file-pdf" iconPos="right" size="small"
                        onClick={() => { setStep(3); doGetSignedDoc(); }} />
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div>
            {loading && !signedDocResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Retrieving signed document...</p>
                    </div>
                </Card>
            )}
            {!loading && !signedDocResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Could not retrieve signed document. Click to retry.</p>
                        <Button label="Retry" icon="pi pi-refresh" outlined onClick={doGetSignedDoc} />
                    </div>
                </Card>
            )}
            {signedDocResult && <GenericSurepassOutputComponent reportData={signedDocResult} title="Signed Document" icon="pi-file-pdf" />}
            <div className="flex justify-content-end mt-3">
                <Button label="Start New Session" icon="pi pi-refresh" severity="secondary" outlined size="small" onClick={resetFlow} />
            </div>
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-pencil"
                        title="E-Sign Flow"
                        subtitle="Upload a PDF, configure signature position, and guide the signer through e-signing — all in one flow"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default EsignFlowComponent;
